import { useEffect } from "react";

/**
 * SEOHead Component
 * Dynamically updates document head with SEO meta tags and structured data
 */
export default function SEOHead({
  title,
  description,
  canonicalUrl,
  type = "website",
  image = "https://www.paradiseborrowed.com/logo.png",
  schema = null
}) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper to set/update meta tag
    const setMeta = (name, content, property = false) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Basic meta tags
    setMeta("description", description);
    
    // Open Graph
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", type, true);
    setMeta("og:image", image, true);
    setMeta("og:site_name", "Paradise Borrowed", true);
    if (canonicalUrl) {
      setMeta("og:url", canonicalUrl, true);
    }

    // Twitter
    setMeta("twitter:card", "summary_large_image", true);
    setMeta("twitter:title", title, true);
    setMeta("twitter:description", description, true);
    setMeta("twitter:image", image, true);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
    }

    // Structured Data (JSON-LD)
    if (schema) {
      // Remove existing schema
      const existingSchema = document.querySelector('script[data-schema="seo"]');
      if (existingSchema) existingSchema.remove();

      // Add new schema
      const schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.setAttribute("data-schema", "seo");
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }

    // Cleanup on unmount
    return () => {
      document.title = "Paradise Borrowed\u2122 | Borrow What You Need";
      const schemaScript = document.querySelector('script[data-schema="seo"]');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, canonicalUrl, type, image, schema]);

  return null; // This component doesn't render anything
}

/**
 * Schema generators for different page types
 */
export const generateLocalBusinessSchema = (location) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": `Paradise Borrowed - ${location.cityName}`,
  "description": `Borrow items from neighbors in ${location.cityName}, ${location.stateName}. Household items, tools, baby gear, and more.`,
  "url": `https://www.paradiseborrowed.com/${location.stateSlug}/${location.citySlug}`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": location.cityName,
    "addressRegion": location.stateAbbrev,
    "addressCountry": "US"
  },
  "priceRange": "$",
  "image": "https://www.paradiseborrowed.com/logo.png",
  "sameAs": [
    "https://www.paradiseborrowed.com"
  ]
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const generateWebPageSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": page.title,
  "description": page.description,
  "url": page.url,
  "isPartOf": {
    "@type": "WebSite",
    "name": "Paradise Borrowed",
    "url": "https://www.paradiseborrowed.com"
  }
});

export const generateItemListSchema = (items, location) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": `Items available in ${location}`,
  "numberOfItems": items.length,
  "itemListElement": items.slice(0, 10).map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Product",
      "name": item.title,
      "description": item.description,
      "image": item.images?.[0],
      "offers": {
        "@type": "Offer",
        "price": item.price_per_day,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": item.price_per_day,
          "priceCurrency": "USD",
          "unitText": "DAY"
        }
      }
    }
  }))
});
